import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { cn } from './cn'

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline'
type Size = 'sm' | 'md' | 'lg' | 'icon'

const variantStyles: Record<Variant, string> = {
  primary:   'bg-accent text-accent-fg hover:bg-accent-hover active:scale-[.98]',
  secondary: 'bg-bg-muted text-fg hover:bg-bg-inset',
  ghost:     'text-fg-muted hover:text-fg hover:bg-bg-muted',
  danger:    'bg-danger text-white hover:bg-danger/90',
  outline:   'border border-border bg-bg-elevated text-fg hover:bg-bg-muted',
}

const sizeStyles: Record<Size, string> = {
  sm:   'h-7 px-2.5 text-xs gap-1',
  md:   'h-8 px-3   text-sm gap-1.5',
  lg:   'h-10 px-4  text-sm gap-2',
  icon: 'h-8 w-8 p-0',
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant
  size?: Size
  leftIcon?: ReactNode
  rightIcon?: ReactNode
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', leftIcon, rightIcon, className, children, ...rest },
  ref
) {
  return (
    <button
      ref={ref}
      className={cn(
        'inline-flex items-center justify-center rounded-md font-medium select-none transition-colors',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/50',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        variantStyles[variant],
        sizeStyles[size],
        className,
      )}
      {...rest}
    >
      {leftIcon && <span className="inline-flex shrink-0">{leftIcon}</span>}
      {children}
      {rightIcon && <span className="inline-flex shrink-0">{rightIcon}</span>}
    </button>
  )
})
